import React from 'react';
import { Music, Disc3 } from 'lucide-react';

/**
 * Modern loading screen component with DJ Nomad branding - Light Theme
 */
const LoadingScreen = ({ 
  message = "Chargement...", 
  variant = "full",
  showLogo = true 
}) => {
  if (variant === "inline") {
    return (
      <div className="flex items-center justify-center gap-3 py-8">
        <div className="relative">
          <Disc3 className="w-6 h-6 text-violet-600 animate-spin" />
        </div>
        <span className="text-gray-500 text-sm">{message}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-600 via-purple-600 to-violet-700 flex flex-col items-center justify-center relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-white/5 to-purple-400/10 rounded-full blur-3xl animate-spin-slow" />
        
        {/* Animated rings */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="w-[300px] h-[300px] border border-white/10 rounded-full animate-ping-slow" />
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="w-[400px] h-[400px] border border-white/5 rounded-full animate-ping-slower" />
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Logo */}
        {showLogo && (
          <div className="mb-8">
            <div className="relative">
              {/* Outer glow ring */}
              <div className="absolute inset-0 -m-4 bg-white/30 rounded-full blur-xl opacity-30 animate-pulse" />
              
              {/* Logo container */}
              <div className="relative flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg">
                <Music className="w-12 h-12 text-white" />
                
                {/* Spinning disc overlay */}
                <div className="absolute inset-2 rounded-xl border-2 border-white/20 animate-spin-slow" />
              </div>
            </div>
          </div>
        )}

        {/* Brand name */}
        <h1 className="text-3xl font-bold text-white mb-2">
          DJ <span className="text-white/90">Nomad</span>
        </h1>

        {/* Loading animation */}
        <div className="flex flex-col items-center mt-8">
          {/* Animated bars (equalizer style) */}
          <div className="flex items-end gap-1 h-8 mb-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-2 bg-white rounded-full animate-equalizer"
                style={{
                  animationDelay: `${i * 0.1}s`,
                  height: '100%',
                }}
              />
            ))}
          </div>

          {/* Loading text */}
          <p className="text-white/80 text-sm animate-pulse">
            {message}
          </p>

          {/* Progress dots */}
          <div className="flex gap-2 mt-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 bg-white rounded-full animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom branding */}
      <div className="absolute bottom-8 text-center">
        <p className="text-white/50 text-xs">
          Powered by <span className="text-white/80">DJ Nomad</span>
        </p>
      </div>
    </div>
  );
};

/**
 * Smaller loading spinner for buttons and inline elements
 */
export const LoadingSpinner = ({ size = "md", className = "" }) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
  };

  return (
    <div className={`${sizeClasses[size]} ${className}`}>
      <Disc3 className="w-full h-full text-violet-600 animate-spin" />
    </div>
  );
};

/**
 * Loading overlay for sections
 */
export const LoadingOverlay = ({ message = "Chargement...", isVisible = true }) => {
  if (!isVisible) return null;

  return (
    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-lg">
      <div className="flex flex-col items-center">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-violet-200 rounded-full" />
          <div className="absolute top-0 left-0 w-12 h-12 border-4 border-transparent border-t-violet-600 rounded-full animate-spin" />
        </div>
        <p className="text-gray-500 text-sm mt-4">{message}</p>
      </div>
    </div>
  );
};

export default LoadingScreen;
